
/* ==========================================================================
   FanScore Photo Layout v7
   Renders the FanScore home as a fixed 100dvh interactive shell matching
   the supplied scoreboard/game-center reference. Existing DOM remains the
   data/function source underneath the shell.
   ========================================================================== */
(() => {
  "use strict";

  const PATH = location.pathname.toLowerCase();
  const IS_FANSCORE =
    PATH.includes("fanscore") ||
    PATH.includes("ring-chaser") ||
    PATH.includes("golden-boot") ||
    PATH.includes("first-and-ten") ||
    PATH.includes("rocket-arm") ||
    PATH.includes("falling-football") ||
    PATH.includes("naming-dudes") ||
    PATH.includes("shot-callers") ||
    PATH.includes("game-log") ||
    !!document.querySelector("[data-fs-root],[data-fs-app],.fs__shell,.fanscore-app");

  if (!IS_FANSCORE) return;

  const $ = (s, root=document) => root.querySelector(s);
  const $$ = (s, root=document) => [...root.querySelectorAll(s)];
  const clean = v => String(v ?? "").replace(/\s+/g," ").trim();
  const esc = v => clean(v).replace(/[&<>"']/g, c => ({
    "&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;","'":"&#39;"
  }[c]));
  const slugify = v => clean(v).toLowerCase().replace(/&/g,"and").replace(/[^a-z0-9]+/g,"-").replace(/^-|-$/g,"");

  const STATES = {
    AL:"Alabama",AK:"Alaska",AZ:"Arizona",AR:"Arkansas",CA:"California",CO:"Colorado",
    CT:"Connecticut",DE:"Delaware",FL:"Florida",GA:"Georgia",HI:"Hawaii",ID:"Idaho",
    IL:"Illinois",IN:"Indiana",IA:"Iowa",KS:"Kansas",KY:"Kentucky",LA:"Louisiana",
    ME:"Maine",MD:"Maryland",MA:"Massachusetts",MI:"Michigan",MN:"Minnesota",MS:"Mississippi",
    MO:"Missouri",MT:"Montana",NE:"Nebraska",NV:"Nevada",NH:"New Hampshire",NJ:"New Jersey",
    NM:"New Mexico",NY:"New York",NC:"North Carolina",ND:"North Dakota",OH:"Ohio",
    OK:"Oklahoma",OR:"Oregon",PA:"Pennsylvania",RI:"Rhode Island",SC:"South Carolina",
    SD:"South Dakota",TN:"Tennessee",TX:"Texas",UT:"Utah",VT:"Vermont",VA:"Virginia",
    WA:"Washington",WV:"West Virginia",WI:"Wisconsin",WY:"Wyoming",DC:"Washington DC"
  };

  /* This is only a fallback. Explicit data-school-state / data-state-code wins. */
  const SCHOOL_STATE = {
    "montana-state":"MT","montana":"MT","utah-tech":"UT","utah-state":"UT","utah":"UT",
    "west-virginia":"WV","wvu":"WV","penn-state":"PA","pittsburgh":"PA","temple":"PA",
    "idaho":"ID","idaho-state":"ID","boise-state":"ID",
    "eastern-washington":"WA","washington":"WA","washington-state":"WA",
    "portland-state":"OR","oregon":"OR","oregon-state":"OR",
    "south-dakota":"SD","south-dakota-state":"SD","north-dakota":"ND","north-dakota-state":"ND",
    "sacramento-state":"CA","cal-poly":"CA","uc-davis":"CA","california":"CA","stanford":"CA",
    "ucla":"CA","usc":"CA","fresno-state":"CA","san-diego-state":"CA","san-jose-state":"CA",
    "northern-arizona":"AZ","arizona":"AZ","arizona-state":"AZ",
    "weber-state":"UT","southern-utah":"UT",
    "northern-colorado":"CO","colorado":"CO","colorado-state":"CO","air-force":"CO",
    "texas":"TX","texas-am":"TX","texas-tech":"TX","tcu":"TX","baylor":"TX","houston":"TX",
    "smu":"TX","rice":"TX","north-texas":"TX","utsa":"TX","utep":"TX","texas-state":"TX",
    "florida":"FL","florida-state":"FL","miami-fl":"FL","ucf":"FL","usf":"FL","fiu":"FL","fau":"FL",
    "georgia":"GA","georgia-tech":"GA","georgia-state":"GA","georgia-southern":"GA",
    "alabama":"AL","auburn":"AL","uab":"AL","south-alabama":"AL","troy":"AL",
    "lsu":"LA","louisiana":"LA","louisiana-tech":"LA","tulane":"LA","ul-monroe":"LA",
    "ole-miss":"MS","mississippi-state":"MS","southern-miss":"MS",
    "tennessee":"TN","vanderbilt":"TN","memphis":"TN","middle-tennessee":"TN",
    "kentucky":"KY","louisville":"KY","western-kentucky":"KY","eastern-kentucky":"KY",
    "ohio-state":"OH","cincinnati":"OH","ohio":"OH","toledo":"OH","miami-oh":"OH","kent-state":"OH",
    "michigan":"MI","michigan-state":"MI","central-michigan":"MI","western-michigan":"MI","eastern-michigan":"MI",
    "indiana":"IN","purdue":"IN","ball-state":"IN","notre-dame":"IN",
    "illinois":"IL","northwestern":"IL","northern-illinois":"IL",
    "wisconsin":"WI","minnesota":"MN","iowa":"IA","iowa-state":"IA","nebraska":"NE",
    "kansas":"KS","kansas-state":"KS","missouri":"MO","arkansas":"AR","arkansas-state":"AR",
    "oklahoma":"OK","oklahoma-state":"OK","tulsa":"OK",
    "new-mexico":"NM","new-mexico-state":"NM","nevada":"NV","unlv":"NV","hawaii":"HI",
    "rutgers":"NJ","maryland":"MD","navy":"MD",
    "virginia":"VA","virginia-tech":"VA","liberty":"VA","old-dominion":"VA","james-madison":"VA",
    "north-carolina":"NC","nc-state":"NC","duke":"NC","wake-forest":"NC","appalachian-state":"NC",
    "east-carolina":"NC","charlotte":"NC","south-carolina":"SC","clemson":"SC","coastal-carolina":"SC",
    "boston-college":"MA","umass":"MA","uconn":"CT","syracuse":"NY","buffalo":"NY","army":"NY",
    "delaware":"DE","marshall":"WV","wyoming":"WY"
  };

  function firstText(selectors){
    for (const s of selectors){
      const el=$(s);
      if(el && clean(el.textContent)) return clean(el.textContent);
    }
    return "";
  }

  function stored(key){
    try{return localStorage.getItem(key)||""}catch(_){return ""}
  }

  function currentTeamSlug(){
    const q=new URLSearchParams(location.search);
    return slugify(
      q.get("team") ||
      q.get("school") ||
      window.FanScore?.teamSlug ||
      window.FanScore?.schoolSlug ||
      window.NILArcadeContext?.teamSlug ||
      stored("fanscore_team_slug") ||
      stored("nil_arcade_team_slug") ||
      ""
    );
  }

  function stateCode(){
    const explicit =
      document.querySelector("[data-school-state]")?.dataset.schoolState ||
      document.querySelector("[data-state-code]")?.dataset.stateCode ||
      document.querySelector("[data-team-state]")?.dataset.teamState ||
      "";
    const ex=clean(explicit).toUpperCase();
    if(STATES[ex]) return ex;

    const slug=currentTeamSlug();
    if(SCHOOL_STATE[slug]) return SCHOOL_STATE[slug];

    const school=slugify(firstText([
      "[data-fs-school-name]","[data-school-name]",".fs__school-name",
      ".fs__team-name",".fsa-scope-title h1"
    ]));
    if(SCHOOL_STATE[school]) return SCHOOL_STATE[school];

    for(const [k,v] of Object.entries(SCHOOL_STATE)){
      if(school && (school===k || school.startsWith(k+"-") || school.includes("-"+k+"-"))) return v;
    }
    return "MT";
  }

  function scoreValue(){
    const selectors=[
      "[data-fs-score]","[data-fanscore-score]","[data-score-total]",
      ".fs__score-value",".fanscore-total",".profile-fanscore"
    ];
    for(const s of selectors){
      for(const el of $$(s)){
        if(el.closest("#fs7-app")) continue;
        const m=clean(el.textContent).replace(/,/g,"").match(/\d+/);
        if(m) return Number(m[0]).toLocaleString();
      }
    }
    return "0";
  }

  function matchup(){
    let team=firstText([
      "[data-fs-team-name]","[data-team-name]",".fs__team-name",
      "[data-fs-school-name]",".fs__school-name"
    ]);
    let opp=firstText([
      "[data-fs-opponent-name]","[data-opponent-name]",".fs__opponent-name"
    ]);

    const upcoming=$("[data-fs-upcoming],.fs__next-game,[data-fs='next-game']");
    const raw=clean(upcoming?.textContent||"");
    if(raw){
      const m=raw.match(/(.{2,44}?)\s+(?:at|@|vs\.?|versus)\s+(.{2,44}?)(?=\s{2,}|$|(?:\b(?:Aug|Sep|Sept|Oct|Nov|Dec|Jan|Feb|Mar|Apr|May|Jun)\b))/i);
      if(m){
        team=team||clean(m[1]);
        opp=opp||clean(m[2]);
      }
    }
    return {team:team||"Montana State",opp:opp||"Utah Tech"};
  }

  function metadata(){
    const bodyText=clean(document.body.textContent);
    const date=
      firstText(["[data-fs-game-date]","[data-game-date]",".fs__game-date"]) ||
      (bodyText.match(/\b(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)[a-z]*\.?\s+\d{1,2},?\s+20\d{2}\b/i)?.[0]||"");
    const time=
      firstText(["[data-fs-game-time]","[data-game-time]",".fs__game-time"]) ||
      (bodyText.match(/\b\d{1,2}:\d{2}\s*(?:AM|PM)(?:\s*[A-Z]{2,4})?\b/i)?.[0]||"");
    const stadium=firstText(["[data-fs-stadium]","[data-stadium]",".fs__stadium",".fs__venue"]);
    const tv=firstText(["[data-fs-tv]","[data-tv]",".fs__broadcast",".fs__tv"]);
    return {
      date:date||"AUG 29, 2026",
      time:time||"8:00 PM MT",
      stadium:stadium||"GREATER ZION STADIUM",
      tv:tv||"ESPN+ / SCRIPPS SPORTS"
    };
  }

  function gameCandidates(){
    const anchors=$$("a").filter(a=>{
      if(a.closest("#fs7-app")) return false;
      const href=(a.getAttribute("href")||"").toLowerCase();
      const name=clean(a.textContent).toLowerCase();
      const cls=String(a.className||"").toLowerCase();
      return (
        cls.includes("competition-game") ||
        a.hasAttribute("data-fs-team-game-link") ||
        href.includes("ring-chaser") ||
        href.includes("golden-boot") ||
        href.includes("first-and-ten") ||
        href.includes("rocket-arm") ||
        href.includes("falling-football") ||
        href.includes("naming-dudes") ||
        href.includes("shot-callers") ||
        name.includes("game log")
      );
    });

    const out=[];
    const seen=new Set();
    for(const a of anchors){
      let href=a.getAttribute("href")||"#";
      const rawTitle=clean(
        a.querySelector("[data-fs-game-name]")?.textContent ||
        a.querySelector("strong")?.textContent ||
        a.getAttribute("aria-label") ||
        a.textContent
      );
      if (/rocket\s*arm/i.test(rawTitle) || /\/pages\/rocket-arm(?:[?#]|$)/i.test(href)) {
        href="/pages/qb-club";
      }
      const title=rawTitle.replace(/\+?\d+\s*fanscore.*$/i,"").trim();
      if(!title) continue;
      const key=(href+"|"+title).toLowerCase();
      if(seen.has(key)) continue;
      seen.add(key);

      const img=a.querySelector("img");
      out.push({
        href,
        title,
        points:clean(a.querySelector("[data-fs-game-points]")?.textContent || a.querySelector("small")?.textContent || ""),
        img:img?.currentSrc || img?.src || "",
        alt:img?.alt || title
      });
    }
    return out;
  }

  function selectGames(){
    const all=gameCandidates();
    const wanted=[
      ["big list","naming dudes"],
      ["hot takes","shot callers"],
      ["golden boot"],
      ["falling football"],
      ["first & ten","first and ten"],
      ["ring chaser"],
      ["rocket arm"],
      ["game log","game logger"]
    ];
    const chosen=[];
    const used=new Set();

    for(const group of wanted){
      const match=all.find((g,i)=>{
        if(used.has(i)) return false;
        const t=g.title.toLowerCase();
        const h=g.href.toLowerCase();
        return group.some(k=>t.includes(k)||h.includes(k.replace(/\s+/g,"-")));
      });
      if(match){
        const idx=all.indexOf(match);
        used.add(idx);
        chosen.push(match);
      }
    }
    all.forEach((g,i)=>{
      if(chosen.length<8 && !used.has(i)){
        used.add(i);chosen.push(g);
      }
    });

    const fallbacks=[
      {title:"The Big List",href:"/pages/naming-dudes"},
      {title:"Hot Takes",href:"/pages/shot-callers"},
      {title:"Golden Boot",href:"/pages/golden-boot"},
      {title:"Falling Footballs",href:"/pages/falling-footballs"},
      {title:"Trivia",href:"/pages/first-and-ten"},
      {title:"Ring Chaser",href:"/pages/ring-chaser"},
      {title:"Rocket Arm",href:"/pages/qb-club"},
      {title:"Game Logger",href:"/pages/fanscore?fsopen=diary"}
    ];
    while(chosen.length<8) chosen.push(fallbacks[chosen.length]);
    return chosen.slice(0,8);
  }

  function gameMarkup(g){
    const points=g.points && !/play$/i.test(g.points) ? g.points : (g.title.toLowerCase().includes("log") ? "LOG MY GAMES" : "+10 FANSCORE / PLAY");
    const art=g.img
      ? `<img src="${esc(g.img)}" alt="${esc(g.alt||"")}" loading="eager">`
      : `<div class="fs7-game-fallback">${esc(g.title)}</div>`;
    return `<a class="fs7-game" href="${esc(g.href||"#")}">
      <span class="fs7-game-art">${art}</span>
      <span class="fs7-game-caption"><b>${esc(g.title)}</b><small>${esc(points)}</small></span>
    </a>`;
  }

  function actionFromExisting(kind){
    const patterns={
      profile:["[data-fs-tab='profile']","[data-fs-open-profile]","a[href*='fsopen=profile']"],
      schedule:["[data-fs-view-schedule]","[data-fs-tab='events']","a[href*='fsopen=events']"],
      checkin:["[data-fs-tab='checkin']","[data-fs-checkin]","a[href*='fsopen=checkin']"],
      team:["[data-fs-team-picker]","[data-fs-open-team]","[data-fs-select-team]"]
    };
    const selectors=patterns[kind]||[];
    for(const s of selectors){
      const el=$$(s).find(x=>!x.closest("#fs7-app"));
      if(el) return ()=>el.click();
    }
    return null;
  }

  function bind(root){
    root.querySelector("[data-fs7-profile]")?.addEventListener("click",e=>{
      const fn=actionFromExisting("profile"); if(fn){e.preventDefault();fn();}
    });
    root.querySelector("[data-fs7-schedule]")?.addEventListener("click",()=>{
      const fn=actionFromExisting("schedule"); if(fn) fn();
    });
    root.querySelector("[data-fs7-checkin]")?.addEventListener("click",()=>{
      const fn=actionFromExisting("checkin"); if(fn) fn();
    });
    root.querySelector("[data-fs7-team]")?.addEventListener("click",()=>{
      const fn=actionFromExisting("team"); if(fn) fn();
    });
    root.querySelector("[data-fs7-dashboard]")?.addEventListener("click",()=>{
      const fn=actionFromExisting("profile");
      const home=$("[data-fs-tab='home']");
      if(home && !home.closest("#fs7-app")) home.click();
    });
  }

  function isHome(){
    if(PATH==="/pages/fanscore" || PATH.includes("/pages/fanscore-home")) return true;
    if(document.querySelector(".fs__competition-grid,.fs__game-grid,[data-fs='grid']")) return true;
    return gameCandidates().length>=4;
  }

  function build(){
    if($("#fs7-app")) return;

    if(!isHome()){
      document.body.classList.add("fs7-photo-subpage");
      return;
    }

    const state=stateCode();
    const stateName=STATES[state]||"Your State";
    const score=scoreValue();
    const m=matchup();
    const meta=metadata();
    const games=selectGames();

    const app=document.createElement("div");
    app.id="fs7-app";
    app.innerHTML=`
      <div class="fs7-frame">
        <header class="fs7-top">
          <div class="fs7-scoreboard" aria-label="FanScore scoreboard">
            <div class="fs7-score-cell"><span>FAN</span><b class="fs7-led">${esc(score.replace(/,/g,"").slice(-2).padStart(2,"0"))}</b></div>
            <div class="fs7-score-cell fs7-score-cell--clock"><span>FANSCORE</span><b class="fs7-led">00:00</b></div>
            <div class="fs7-score-cell"><span>GAME</span><b class="fs7-led">01</b></div>
          </div>

          <button class="fs7-state-selector" type="button" data-fs7-team>
            <span class="fs7-pin">●</span>
            <span>${esc(stateName)}</span>
            <span class="fs7-chevron">▼</span>
          </button>

          <div class="fs7-user-area">
            <button class="fs7-avatar" type="button" data-fs7-profile aria-label="My profile">●</button>
            <span class="fs7-battery">66% ▰</span>
          </div>
        </header>

        <section class="fs7-game-panel">
          <div class="fs7-game-main">
            <section class="fs7-fanscore">
              <div class="fs7-band">Your Fan Score</div>
              <div class="fs7-score-body">
                <div class="fs7-trophy">🏆</div>
                <div class="fs7-total" data-fs7-score>${esc(score)}</div>
                <div class="fs7-total-label">Total Points</div>
                <a class="fs7-view-profile" href="/pages/fanscore?fsopen=profile" data-fs7-profile>View Profile ›</a>
              </div>
            </section>

            <section class="fs7-next">
              <div class="fs7-band">Next Up</div>
              <div class="fs7-next-body">
                <div class="fs7-match">
                  <div class="fs7-state-mark" title="${esc(stateName)}"><span>${esc(state)}</span></div>
                  <div class="fs7-matchup">
                    <span class="fs7-team">${esc(m.team)}</span>
                    <span class="fs7-at">AT</span>
                    <span class="fs7-team">${esc(m.opp)}</span>
                  </div>
                </div>

                <div class="fs7-meta">
                  <div class="fs7-meta-item">
                    <span class="fs7-meta-icon">▣</span>
                    <span class="fs7-meta-copy"><b>${esc(meta.date)}</b><small>Game Date</small></span>
                  </div>
                  <div class="fs7-meta-item">
                    <span class="fs7-meta-icon">◷</span>
                    <span class="fs7-meta-copy"><b>${esc(meta.time)}</b><small>Kickoff / Deadline</small></span>
                  </div>
                  <div class="fs7-meta-item">
                    <span class="fs7-meta-icon">⌖</span>
                    <span class="fs7-meta-copy"><b>${esc(meta.stadium)}</b><small>Stadium</small></span>
                  </div>
                </div>
              </div>
            </section>
          </div>

          <div class="fs7-utility-row">
            <div class="fs7-utility"><span class="fs7-util-icon">▣</span><span class="fs7-util-copy"><b>Watch Live</b><small>${esc(meta.tv)}</small></span></div>
            <button class="fs7-utility" type="button" data-fs7-checkin><span class="fs7-util-icon">⌖</span><span class="fs7-util-copy"><b>Check-In</b><small>Earn Points</small></span></button>
            <button class="fs7-utility" type="button" data-fs7-schedule><span class="fs7-util-icon">▦</span><span class="fs7-util-copy"><b>View Schedule</b><small>Full Season</small></span></button>
          </div>
        </section>

        <div class="fs7-marquee-wrap">
          <span class="fs7-marquee-lines"><i></i><i></i></span>
          <div class="fs7-marquee">The Game Center</div>
          <span class="fs7-marquee-lines"><i></i><i></i></span>
        </div>

        <section class="fs7-games-zone">
          <div class="fs7-games">
            ${games.map(gameMarkup).join("")}
          </div>
        </section>

        <nav class="fs7-bottom" aria-label="FanScore navigation">
          <button class="fs7-bottom-btn" type="button" data-fs7-dashboard>
            <span class="fs7-bottom-icon">◴</span><span>Dashboard</span>
          </button>
          <button class="fs7-state-button" type="button" data-fs7-team aria-label="Select team">
            <span>${esc(state)}</span>
          </button>
          <button class="fs7-bottom-btn" type="button" data-fs7-profile>
            <span class="fs7-bottom-icon">♙</span><span>My Profile</span>
          </button>
        </nav>

        <footer class="fs7-footer">
          <div class="fs7-kind">Be Kind<br>Please</div>
          <div class="fs7-thanks">Thank You! &nbsp; Have A Great Game!</div>
        </footer>
      </div>`;

    document.body.appendChild(app);
    document.body.classList.add("fs7-photo-home","fs7-ready");
    bind(app);
  }

  function update(){
    const el=$("[data-fs7-score]");
    if(el){
      const v=scoreValue();
      if(v && v!=="0") el.textContent=v;
    }
  }

  function boot(){
    build();
    update();

    let t=0;
    const obs=new MutationObserver(()=>{
      clearTimeout(t);
      t=setTimeout(update,120);
    });
    obs.observe(document.documentElement,{subtree:true,childList:true,characterData:true});
  }

  if(document.readyState==="loading") document.addEventListener("DOMContentLoaded",()=>setTimeout(boot,70),{once:true});
  else setTimeout(boot,70);
})();
